/**
 * TEMPORARY local-only dev API mock — DO NOT COMMIT.
 *
 * Companion to devAuth.ts. When the dev auth bypass is active (no real Stytch
 * keys), there is usually no Django backend on :8000 either, so every write/read
 * fails ("Failed to save contract"). This patches window.fetch to serve an
 * in-memory (localStorage-backed) mock for the Contracts endpoints so you can
 * exercise the UI with no backend.
 *
 * Scope: full CRUD for /contracts; seeded read data for /revenue/statements
 * (list + details tabs + mark-as-paid/delete) and /imports/vendors. Any other
 * API call returns an empty paginated response (GET) or {} (writes) so the
 * surrounding UI doesn't crash. This is a dev convenience, NOT a backend replacement.
 *
 * Installed only from seedDevAuth() (which is gated on import.meta.env.DEV +
 * placeholder keys), so it is completely inert in production builds.
 *
 * git-excluded (see .git/info/exclude). To remove: delete this file and the
 * installDevApiMock() call in devAuth.ts.
 */
import { API_BASE_URL } from "@/lib/api/core";

const STORE_KEY = "dev-mock-contracts";

function loadContracts(): any[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveContracts(list: any[]): void {
  localStorage.setItem(STORE_KEY, JSON.stringify(list));
}

function jsonResponse(data: unknown, status = 200): Response {
  if (status === 204) return new Response(null, { status });
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

const emptyList = { results: [], count: 0, next: null, previous: null };

/** Wrap an array as a DRF-style paginated response ({ results, count, next, previous }). */
function paginated(results: unknown[]): object {
  return { results, count: results.length, next: null, previous: null };
}

// A few sample payees so the "Add existing payees…" combobox has data and the
// "Active" status flow (which requires ≥1 payee) can be exercised.
const DEV_PAYEES = [
  {
    id: "payee-1",
    name: "Alice Producer",
    legal_name: "Alice Productions LLC",
    is_active: true,
    preferred_currency: "USD",
    country: "US",
    city: "Los Angeles",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-2",
    name: "Bob Songwriter",
    legal_name: "Bob Music Inc.",
    is_active: true,
    preferred_currency: "USD",
    country: "GB",
    city: "London",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-3",
    name: "Carol Mixer",
    legal_name: null,
    is_active: true,
    preferred_currency: "EUR",
    country: "DE",
    city: "Berlin",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
];

function payeeName(id: string): string {
  return DEV_PAYEES.find((p) => p.id === id)?.name ?? id;
}

// --- Royalty "Create new statement" scenario contracts ---------------------
// Each contract is a complete, generatable contract (tracks + 50/50 payees) that
// drives one variant of the create-statement right panel. The scenario is named
// in the contract title so it's obvious which case is being previewed.
// `revenue_statements_preview` feeds the "Revenue statements for this period"
// table; `has_revenue`/`has_expenses` drive the amber period warnings.
function scenarioContract(opts: {
  id: string;
  name: string;
  has_revenue: boolean;
  has_expenses: boolean;
  revenue_statements_preview: { name: string; period: string; status: "settled" | "draft" }[];
}) {
  return {
    id: opts.id,
    name: opts.name,
    status: "active",
    contract_type: "exclusive_license",
    currency: "USD",
    start_date: "2024-01-01",
    end_date: null,
    territory: "Worldwide",
    statement_period: "quarterly",
    starting_balance: null,
    label: "dev-label",
    label_name: "Dev Label",
    party: "No.19 Music",
    tracks_count: 1,
    all_tracks_have_isrc: true,
    first_sound_recording_title: "Tripmastaz - While U Pretend (Original Mix)",
    can_delete: false,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    payees: [
      {
        matched: true,
        payee: { id: "payee-adele", name: "Adele Laurie Blue Adkins" },
        share_percentage: 40,
        opening_balance: null,
        // Below-threshold balances carried from prior periods, settled in this payout.
        // The "Carried forward" total is the SUM of these (120 + 95.50 + 105 = 320.50).
        carried_statements: [
          { name: "Spotify Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "120.00" },
          { name: "Apple Music Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "95.50" },
          { name: "YouTube Q2 2025", period: "Apr 1 - Jun 30, 2025", amount: "105.00" },
        ],
      },
      {
        matched: true,
        payee: { id: "payee-beyonce", name: "Beyoncé Giselle Knowles-Carter" },
        share_percentage: 35,
        opening_balance: null,
        // Single prior unpaid below-threshold statement.
        carried_statements: [
          { name: "Spotify Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "75.00" },
        ],
      },
      {
        matched: true,
        payee: { id: "payee-james", name: "James Blake Litherland" },
        share_percentage: 25,
        opening_balance: null,
        // Nothing carried forward.
        carried_statements: [],
      },
    ],
    has_revenue: opts.has_revenue,
    has_expenses: opts.has_expenses,
    revenue_statements_preview: opts.revenue_statements_preview,
    related_recordings: [],
    related_releases: [],
  };
}

const SCENARIO_PERIOD = "Oct 1, 2025 - Dec 31, 2025";

const DEV_SCENARIO_CONTRACTS = [
  scenarioContract({
    id: "roy-scn-settled",
    name: "Back in Black Agreement 1481 — Settled",
    has_revenue: true,
    has_expenses: true,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "settled" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-draft",
    name: "Back in Black Agreement 1482 — Draft revenue",
    has_revenue: true,
    has_expenses: true,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "draft" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-no-revenue",
    name: "Back in Black Agreement 1483 — No revenue",
    has_revenue: false,
    has_expenses: true,
    revenue_statements_preview: [],
  }),
  scenarioContract({
    id: "roy-scn-no-expenses",
    name: "Back in Black Agreement 1484 — No expenses",
    has_revenue: true,
    has_expenses: false,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "settled" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-none",
    name: "Back in Black Agreement 1485 — No revenue or expenses",
    has_revenue: false,
    has_expenses: false,
    revenue_statements_preview: [],
  }),
];

// --- Revenue statements mock data -----------------------------------------
// Shapes mirror the real API: SupportedVendor (/imports/vendors, a bare array)
// and RevenueStatement (/revenue/statements, paginated). Statement `vendor`
// values reference DEV_VENDORS ids so the details Metadata resolves a name.

const DEV_VENDORS = [
  { id: "vendor-above-board", name: "Above Board", default_currency: "GBP" },
  { id: "vendor-create-music-group", name: "Create Music Group", default_currency: "USD" },
  { id: "vendor-eastgate-records", name: "Eastgate Records", default_currency: "USD" },
  { id: "vendor-k7", name: "K7", default_currency: "EUR" },
  { id: "vendor-label-engine", name: "Label Engine", default_currency: "" },
  { id: "vendor-label-worx", name: "Label Worx", default_currency: "USD" },
  { id: "vendor-northbound-digital", name: "Northbound Digital", default_currency: "USD" },
  { id: "vendor-symphonic", name: "Symphonic Distribution", default_currency: "USD" },
  { id: "vendor-orchard-legacy", name: "The Orchard (Legacy Format)", default_currency: "GBP" },
  { id: "vendor-orchard-2025", name: "The Orchard (New Version 2025)", default_currency: "GBP" },
];


// Placeholder invoice file so the "Download" action shows up in the UI (mock).
const DEV_INVOICE_FILE =
  "data:text/csv;charset=utf-8,Track%2CISRC%2CRevenue%0AMidnight%20Drive%2CUSRC12400001%2C3120.45";

// --- Revenue statements dataset ------------------------------------------
// Four scenarios, one vendor each, all in the label currency (USD) so nothing
// is muddied by FX. Every scenario exists to be walked through in the settle
// drawer — see SCENARIOS in src/prototypes/multi-statement-settlement.
//
//  main  Create Music Group   one payment, several statements
//  01    Label Worx           the payment is larger than the selection
//  02    Northbound Digital   more selected than the payment covers
//  03    Eastgate Records     the statement is a correction
//  04    Symphonic            the amounts match exactly
//
// Each scenario owns its vendor outright, so walking one never settles another
// one's statements.
//
// Bump the version suffix whenever the seed shape changes, so the browser
// re-seeds instead of using a stale localStorage copy.
const REVENUE_STORE_KEY = "dev-mock-revenue-mss-v6";

function monthStatement(opts: {
  id: string;
  vendor: string;
  vendorName: string;
  month: string;
  monthLabel: string;
  lastDay: string;
  revenue: string;
  settledAt?: string;
}) {
  return {
    id: opts.id,
    file_name: `${opts.id}.csv`,
    import_batch: `batch-${opts.id}`,
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: opts.vendor,
    currency: "USD",
    period_start: `${opts.month}-01`,
    period_end: `${opts.month}-${opts.lastDay}`,
    file: DEV_INVOICE_FILE,
    status: "ready",
    total_lines: 120,
    revenue: opts.revenue,
    created_at: `${opts.month}-${opts.lastDay}T10:00:00Z`,
    updated_at: `${opts.month}-${opts.lastDay}T10:00:00Z`,
    name: `${opts.vendorName} — ${opts.monthLabel}`,
    can_delete: true,
    // Settled statements carry the amount that actually landed; drafts do not.
    amount_received: opts.settledAt ? opts.revenue : null,
    exchange_rate: null,
    date_received: opts.settledAt ?? null,
  };
}

const DEV_REVENUE_STATEMENTS = [
  // main · Create Music Group — one payment covering several statements.
  // Jan is already settled; Feb, the Mar correction and Apr are outstanding.
  // Opening Apr with 150.00 pulls in Feb and Mar (75.86 + 89.39 − 12.40 = 152.85).
  monthStatement({
    id: "cmg-2026-01",
    vendor: "vendor-create-music-group",
    vendorName: "Create Music Group",
    month: "2026-01",
    monthLabel: "Jan 2026",
    lastDay: "31",
    revenue: "170.04",
    settledAt: "2026-02-06",
  }),
  monthStatement({
    id: "cmg-2026-02",
    vendor: "vendor-create-music-group",
    vendorName: "Create Music Group",
    month: "2026-02",
    monthLabel: "Feb 2026",
    lastDay: "28",
    revenue: "89.39",
  }),
  // A correction inside the main flow: a negative value, so it can only be
  // settled alongside a later statement.
  monthStatement({
    id: "cmg-2026-03",
    vendor: "vendor-create-music-group",
    vendorName: "Create Music Group",
    month: "2026-03",
    monthLabel: "Mar 2026",
    lastDay: "31",
    revenue: "-12.40",
  }),
  monthStatement({
    id: "cmg-2026-04",
    vendor: "vendor-create-music-group",
    vendorName: "Create Music Group",
    month: "2026-04",
    monthLabel: "Apr 2026",
    lastDay: "30",
    revenue: "75.86",
  }),

  // 04 · Symphonic Distribution — the only outstanding statement for this
  // vendor, so settling it never offers an allocation.
  monthStatement({
    id: "sym-2026-04",
    vendor: "vendor-symphonic",
    vendorName: "Symphonic Distribution",
    month: "2026-04",
    monthLabel: "Apr 2026",
    lastDay: "30",
    revenue: "4180.20",
  }),

  // 01 · Label Worx — 320.00 outstanding in total, so a 500.00 payment is more
  // than everything on the books.
  monthStatement({
    id: "lwx-2026-02",
    vendor: "vendor-label-worx",
    vendorName: "Label Worx",
    month: "2026-02",
    monthLabel: "Feb 2026",
    lastDay: "28",
    revenue: "200.00",
  }),
  monthStatement({
    id: "lwx-2026-03",
    vendor: "vendor-label-worx",
    vendorName: "Label Worx",
    month: "2026-03",
    monthLabel: "Mar 2026",
    lastDay: "31",
    revenue: "120.00",
  }),

  // 02 · Northbound Digital — Jan is far too big for a small payment, so the
  // suggestion skips it and ticking it by hand overshoots.
  monthStatement({
    id: "nbd-2026-01",
    vendor: "vendor-northbound-digital",
    vendorName: "Northbound Digital",
    month: "2026-01",
    monthLabel: "Jan 2026",
    lastDay: "31",
    revenue: "766.38",
  }),
  monthStatement({
    id: "nbd-2026-02",
    vendor: "vendor-northbound-digital",
    vendorName: "Northbound Digital",
    month: "2026-02",
    monthLabel: "Feb 2026",
    lastDay: "28",
    revenue: "89.39",
  }),
  // 03 · Eastgate Records — a correction on its own vendor. Opening Feb explains
  // that it cannot be settled alone; opening Mar with 268.80 clears both
  // (310.00 − 41.20).
  monthStatement({
    id: "eas-2026-02",
    vendor: "vendor-eastgate-records",
    vendorName: "Eastgate Records",
    month: "2026-02",
    monthLabel: "Feb 2026",
    lastDay: "28",
    revenue: "-41.20",
  }),
  monthStatement({
    id: "eas-2026-03",
    vendor: "vendor-eastgate-records",
    vendorName: "Eastgate Records",
    month: "2026-03",
    monthLabel: "Mar 2026",
    lastDay: "31",
    revenue: "310.00",
  }),

  monthStatement({
    id: "nbd-2026-03",
    vendor: "vendor-northbound-digital",
    vendorName: "Northbound Digital",
    month: "2026-03",
    monthLabel: "Mar 2026",
    lastDay: "31",
    revenue: "75.86",
  }),
];

/**
 * A fresh copy of the seed. Never hand out the module-level array itself: the
 * first write after seeding would mutate it in place, and every later "reset"
 * would then restore the already-changed data.
 */
function seedCopy(): any[] {
  return JSON.parse(JSON.stringify(DEV_REVENUE_STATEMENTS));
}

function loadRevenue(): any[] {
  const key = REVENUE_STORE_KEY;
  const raw = localStorage.getItem(key);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* fall through to reseed */
    }
  }
  const seed = seedCopy();
  localStorage.setItem(key, JSON.stringify(seed));
  return seed;
}

function saveRevenue(list: any[]): void {
  localStorage.setItem(REVENUE_STORE_KEY, JSON.stringify(list));
}

// --- Settlement split -----------------------------------------------------
// Stands in for the backend calculation behind /settlement-preview and /settle.
// One payment, one difference: it is spread across the settled statements pro
// rata by statement value, and the rounding remainder lands on the largest one
// so the parts always add back up to the payment.

/** Label currency of the mock organisation (see DEV_LABEL in devAuth.ts). */
const LABEL_CURRENCY = "USD";

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Statement value in the label currency — the import-time FX estimate when it was reported in another one. */
function labelValue(statement: any): number {
  const raw =
    statement.currency === LABEL_CURRENCY
      ? statement.revenue
      : (statement.estimated_amount_received ?? statement.revenue);
  const n = parseFloat(raw);
  return Number.isNaN(n) ? 0 : n;
}

export function settlementSplit(ids: unknown, amountReceivedRaw: unknown): any | null {
  const amountReceived = parseFloat(String(amountReceivedRaw));
  if (!Array.isArray(ids) || ids.length === 0 || Number.isNaN(amountReceived)) return null;

  const list = loadRevenue();
  const selected = ids.map((id) => list.find((s) => s.id === id));
  if (selected.some((s) => !s)) return null;

  const values = selected.map((s) => labelValue(s));
  const totalValue = round2(values.reduce((sum, v) => sum + v, 0));
  const difference = round2(amountReceived - totalValue);

  const shares = values.map((v) => (totalValue === 0 ? 0 : round2(difference * (v / totalValue))));
  const remainder = round2(difference - shares.reduce((sum, v) => sum + v, 0));
  if (remainder !== 0 && shares.length > 0) {
    let largest = 0;
    for (let i = 1; i < values.length; i++) if (values[i] > values[largest]) largest = i;
    shares[largest] = round2(shares[largest] + remainder);
  }

  return {
    currency: LABEL_CURRENCY,
    amount_received: amountReceived.toFixed(2),
    total_value: totalValue.toFixed(2),
    difference: difference.toFixed(2),
    rows: selected.map((statement, i) => ({
      statement: statement.id,
      name: statement.name || statement.file_name,
      period_start: statement.period_start ?? null,
      period_end: statement.period_end ?? null,
      value: values[i].toFixed(2),
      difference: shares[i].toFixed(2),
      received: round2(values[i] + shares[i]).toFixed(2),
    })),
  };
}

// --- Prototype-only dev affordances --------------------------------------
// Exposed so the dev bar can offer "Reset prototype", and so a failed settle is
// reachable at all. Both are dev/demo conveniences and are inert in any build
// where the mock isn't installed.
const SIMULATE_SETTLE_ERROR_KEY = "dev-mock-revenue-simulate-settle-error";

/** Put every statement back to its seeded state. */
export function resetRevenueMock(): void {
  localStorage.setItem(REVENUE_STORE_KEY, JSON.stringify(seedCopy()));
}

/**
 * Put just these statements back to their seeded state, leaving every other one
 * alone. Picking a scenario uses this, so a scenario is always walkable from the
 * start without wiping what was done elsewhere.
 */
export function resetStatements(ids: string[]): void {
  const wanted = new Set(ids);
  const seed = seedCopy();
  const list = loadRevenue().map((statement) => {
    if (!wanted.has(statement.id)) return statement;
    return seed.find((original) => original.id === statement.id) ?? statement;
  });
  saveRevenue(list);
}

/** When on, the next mark-as-settled PATCH is forced to fail (500). */
export function isSimulateSettleError(): boolean {
  return localStorage.getItem(SIMULATE_SETTLE_ERROR_KEY) === "true";
}

export function setSimulateSettleError(on: boolean): void {
  if (on) localStorage.setItem(SIMULATE_SETTLE_ERROR_KEY, "true");
  else localStorage.removeItem(SIMULATE_SETTLE_ERROR_KEY);
}

// --- Import batch mock ("Upload new statement" wizard) -------------------
// Auto-import path. Status advances by elapsed time so the spinners show briefly:
// analyzing → analyzed → (auto-confirm) → importing → completed.
const IMPORT_BATCHES_KEY = "dev-mock-import-batches";
const IMPORT_PHASE_MS = 2500;

// No catalog changes → can_auto_import lets the wizard skip the review step.
const EMPTY_CATALOG_PREVIEW = {
  can_import: true,
  can_auto_import: true,
  releases_to_create: [],
  releases_to_update: [],
  likely_releases: [],
  conflicts_releases: [],
  recordings_to_create: [],
  recordings_to_update: [],
  likely_recordings: [],
  conflicts_recordings: [],
  errors: { release: [], recordings: [] },
  summary: {
    new_releases: 0,
    updated_releases: 0,
    new_recordings: 0,
    updated_recordings: 0,
    likely_recordings: 0,
    likely_releases: 0,
    conflicts_recordings: 0,
    conflicts_releases: 0,
    errors: 0,
  },
};

const DEV_IMPORT_SUMMARY = {
  revenue_lines: 1284,
  releases_created: 0,
  recordings_created: 0,
  recordings_updated: 0,
  releases_updated: 0,
};

function loadImportBatches(): Record<string, any> {
  try {
    return JSON.parse(localStorage.getItem(IMPORT_BATCHES_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveImportBatches(map: Record<string, any>): void {
  localStorage.setItem(IMPORT_BATCHES_KEY, JSON.stringify(map));
}

function stripInternal(batch: any): any {
  const { _phaseStart, ...rest } = batch;
  return rest;
}

function createImportBatch(body: any): any {
  const now = new Date();
  const id = crypto.randomUUID();
  const batch = {
    id,
    label: body?.label ?? "dev-label",
    name: body?.name ?? null,
    vendor: body?.vendor ?? null,
    file_name: body?.file ?? "revenue_statement.csv",
    file_size: 12480,
    status: "analyzing",
    progress: 0,
    counters: null,
    error: null,
    created_at: now.toISOString(),
    updated_at: now.toISOString(),
    _phaseStart: now.getTime(),
  };
  const map = loadImportBatches();
  map[id] = batch;
  saveImportBatches(map);
  return stripInternal(batch);
}

function pollImportBatch(id: string): any | null {
  const map = loadImportBatches();
  const batch = map[id];
  if (!batch) return null;
  const elapsed = new Date().getTime() - (batch._phaseStart ?? 0);
  if (batch.status === "analyzing" && elapsed >= IMPORT_PHASE_MS) {
    batch.status = "analyzed";
    batch.progress = 100;
    batch.updated_at = new Date().toISOString();
    batch._phaseStart = new Date().getTime();
    saveImportBatches(map);
  } else if (batch.status === "importing" && elapsed >= IMPORT_PHASE_MS) {
    batch.status = "completed";
    batch.progress = 100;
    batch.updated_at = new Date().toISOString();
    saveImportBatches(map);
  }
  return stripInternal(batch);
}

function advanceImportBatch(id: string, status: string): any | null {
  const map = loadImportBatches();
  const batch = map[id];
  if (!batch) return null;
  batch.status = status;
  batch.progress = 0;
  batch.updated_at = new Date().toISOString();
  batch._phaseStart = new Date().getTime();
  saveImportBatches(map);
  return stripInternal(batch);
}

// Per-statement detail rows. The same set is served for every statement (enough
// to populate the three details tabs); shapes match TopTrack / TrackRevenue and
// ContributorsRevenueItem.
const DEV_STATEMENT_TRACKS = [
  {
    id: "track-1",
    title: "Midnight Drive",
    isrc: "USRC12400001",
    track_number: "1",
    revenue: "3120.45",
    quantity: 412000,
    currency: "USD",
    contributors: [
      { id: "c-1", full_name: "Alice Producer", display_name: "Alice", role: "Producer" },
    ],
    release: { id: "rel-1", title: "Neon Skies", artwork: null },
  },
  {
    id: "track-2",
    title: "Paper Planes",
    isrc: "USRC12400002",
    track_number: "2",
    revenue: "2487.10",
    quantity: 331500,
    currency: "USD",
    contributors: [
      { id: "c-2", full_name: "Bob Songwriter", display_name: "Bob", role: "Songwriter" },
    ],
    release: { id: "rel-1", title: "Neon Skies", artwork: null },
  },
  {
    id: "track-3",
    title: "Coastline",
    isrc: "USRC12400003",
    track_number: "1",
    revenue: "1894.77",
    quantity: 256800,
    currency: "USD",
    contributors: [
      { id: "c-1", full_name: "Alice Producer", display_name: "Alice", role: "Producer" },
      { id: "c-3", full_name: "Carol Mixer", display_name: "Carol", role: "Mixer" },
    ],
    release: { id: "rel-2", title: "Open Water", artwork: null },
  },
  {
    id: "track-4",
    title: "Slow Burn",
    isrc: "USRC12400004",
    track_number: "3",
    revenue: "1042.00",
    quantity: 142300,
    currency: "USD",
    contributors: [
      { id: "c-2", full_name: "Bob Songwriter", display_name: "Bob", role: "Songwriter" },
    ],
    release: { id: "rel-2", title: "Open Water", artwork: null },
  },
];

const DEV_STATEMENT_CONTRIBUTORS = [
  {
    id: "contrib-rev-1",
    revenue: "5014.45",
    currency: "USD",
    quantity: 668300,
    contributors: [{ id: "c-1", name: "Alice Producer", country: "US" }],
  },
  {
    id: "contrib-rev-2",
    revenue: "3529.10",
    currency: "USD",
    quantity: 473800,
    contributors: [{ id: "c-2", name: "Bob Songwriter", country: "GB" }],
  },
  {
    id: "contrib-rev-3",
    revenue: "1894.77",
    currency: "USD",
    quantity: 256800,
    contributors: [{ id: "c-3", name: "Carol Mixer", country: "DE" }],
  },
];

// Build a fully-shaped Contract (matches the `Contract`/`ContractDetails` types)
// from a POST/PUT body, merging onto an existing record for updates.
function buildContract(body: any, existing?: any): any {
  const now = new Date().toISOString();
  const payees = Array.isArray(body?.payees)
    ? body.payees.map((p: any) => ({
        matched: true,
        payee: { id: p.payee, name: payeeName(p.payee) },
        share_percentage: p.share_percentage ?? null,
        opening_balance: p.opening_balance ?? null,
      }))
    : (existing?.payees ?? []);

  return {
    id: existing?.id ?? crypto.randomUUID(),
    created_at: existing?.created_at ?? now,
    updated_at: now,
    name: body?.name ?? existing?.name ?? "Untitled contract",
    contract_type: body?.contract_type ?? existing?.contract_type,
    status: body?.status ?? existing?.status ?? "draft",
    start_date: body?.start_date ?? existing?.start_date ?? null,
    end_date: body?.end_date ?? existing?.end_date ?? null,
    territory: body?.territory ?? existing?.territory,
    currency: body?.currency ?? existing?.currency ?? "USD",
    label: body?.label ?? existing?.label ?? "dev-label",
    label_name: "Dev Label",
    party: existing?.party ?? "",
    label_share_percent:
      body?.label_share_percent != null
        ? String(body.label_share_percent)
        : (existing?.label_share_percent ?? null),
    payment_threshold: body?.payment_threshold ?? existing?.payment_threshold ?? null,
    advance_amount: body?.advance_amount ?? existing?.advance_amount ?? null,
    statement_period: body?.statement_period ?? existing?.statement_period,
    starting_balance: existing?.starting_balance ?? null,
    payees,
    releases_count: existing?.releases_count ?? 0,
    tracks_count: existing?.tracks_count ?? 0,
    all_tracks_have_isrc: true,
    first_sound_recording_title: null,
    can_delete: true,
  };
}

// Returns a Response for handled routes, or null to fall through to real fetch.
// --- Royalty statements mock --------------------------------------------
// Reproduces the current Royalty Statements view on dummy data: list + detail
// + HTML preview + row actions (mark sent / mark paid / delete).
const ROYALTY_STORE_KEY = "dev-mock-royalty-statements-v2";

function royaltyPayeeDetails(id: string, name: string, currency: string, country: string) {
  return {
    id,
    name,
    legal_name: `${name} LLC`,
    preferred_currency: currency,
    country,
    contact_email: `${name.split(" ")[0].toLowerCase()}@example.com`,
    is_active: true,
  };
}

function royaltyContractDetails(id: string, name: string, currency: string) {
  return {
    id,
    name,
    contract_type: "recording",
    status: "active",
    start_date: "2024-01-01",
    end_date: null,
    currency,
  };
}

const DEV_ROYALTY_STATEMENTS = [
  {
    id: "roy-1",
    statement_no: "RS-2025-0001",
    payee: "payee-1",
    payee_details: royaltyPayeeDetails("payee-1", "Alice Producer", "USD", "US"),
    contract: "contract-1",
    contract_details: royaltyContractDetails("contract-1", "Alice — Recording Agreement", "USD"),
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    statement_balance: "4210.55",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2025-04-12T10:00:00Z",
    updated_at: "2025-04-14T10:00:00Z",
  },
  {
    id: "roy-2",
    statement_no: "RS-2025-0002",
    payee: "payee-2",
    payee_details: royaltyPayeeDetails("payee-2", "Bob Songwriter", "USD", "GB"),
    contract: "contract-2",
    contract_details: royaltyContractDetails("contract-2", "Bob — Publishing Deal", "USD"),
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    statement_balance: "3180.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "paid",
    created_at: "2025-04-12T10:05:00Z",
    updated_at: "2025-05-02T09:00:00Z",
  },
  {
    id: "roy-3",
    statement_no: "RS-2025-0003",
    payee: "payee-3",
    payee_details: royaltyPayeeDetails("payee-3", "Carol Mixer", "USD", "DE"),
    contract: "contract-1",
    contract_details: royaltyContractDetails("contract-1", "Alice — Recording Agreement", "USD"),
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    statement_balance: "980.20",
    currency: "USD",
    file: null,
    file_url: null,
    created_by: "dev@local.test",
    status: "draft",
    created_at: "2025-04-12T10:10:00Z",
    updated_at: "2025-04-12T10:10:00Z",
  },
  {
    id: "roy-4",
    statement_no: "RS-2025-0004",
    payee: "payee-1",
    payee_details: royaltyPayeeDetails("payee-1", "Alice Producer", "USD", "US"),
    contract: "contract-1",
    contract_details: royaltyContractDetails("contract-1", "Alice — Recording Agreement", "USD"),
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    statement_balance: "5620.40",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "draft",
    created_at: "2025-01-15T10:00:00Z",
    updated_at: "2025-01-15T10:00:00Z",
  },
  {
    id: "roy-5",
    statement_no: "RS-2024-0019",
    payee: "payee-2",
    payee_details: royaltyPayeeDetails("payee-2", "Bob Songwriter", "USD", "GB"),
    contract: "contract-2",
    contract_details: royaltyContractDetails("contract-2", "Bob — Publishing Deal", "USD"),
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    statement_balance: "1450.75",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2025-01-15T10:05:00Z",
    updated_at: "2025-01-18T10:05:00Z",
  },
  {
    id: "roy-6",
    statement_no: "RS-2024-0020",
    payee: "payee-3",
    payee_details: royaltyPayeeDetails("payee-3", "Carol Mixer", "USD", "DE"),
    contract: "contract-1",
    contract_details: royaltyContractDetails("contract-1", "Alice — Recording Agreement", "USD"),
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    statement_balance: "2310.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "paid",
    created_at: "2025-01-15T10:10:00Z",
    updated_at: "2025-02-01T10:10:00Z",
  },
];

function loadRoyalty(): any[] {
  const raw = localStorage.getItem(ROYALTY_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(DEV_ROYALTY_STATEMENTS));
  return DEV_ROYALTY_STATEMENTS;
}

function saveRoyalty(list: any[]): void {
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(list));
}

function royaltyStatementHtml(s: any): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:ui-sans-serif,system-ui,sans-serif;color:#111;padding:32px;max-width:720px;margin:0 auto}
    h1{font-size:20px;margin:0 0 4px} .muted{color:#666;font-size:13px}
    table{width:100%;border-collapse:collapse;margin-top:24px;font-size:14px}
    th,td{text-align:left;padding:8px 0;border-bottom:1px solid #eee} td.r,th.r{text-align:right}
    .total{font-weight:600;font-size:16px}
  </style></head><body>
    <h1>Royalty statement ${s.statement_no}</h1>
    <div class="muted">${s.payee_details?.name ?? ""} · ${s.contract_details?.name ?? ""}</div>
    <div class="muted">Period: ${s.period_start} – ${s.period_end}</div>
    <table>
      <tr><th>Description</th><th class="r">Amount</th></tr>
      <tr><td>Net royalties for period</td><td class="r">${s.statement_balance} ${s.currency}</td></tr>
      <tr><td class="total">Amount due</td><td class="r total">${s.statement_balance} ${s.currency}</td></tr>
    </table>
  </body></html>`;
}

function route(method: string, path: string, body: any, query: URLSearchParams): Response | null {
  const trimmed = path.replace(/\/+$/, "");

  // Payees live under /contracts/payees — match BEFORE the /contracts/:id route
  // below, otherwise "payees" would be treated as a contract id.
  if (trimmed === "/contracts/payees") {
    if (method === "GET") {
      return jsonResponse({
        results: DEV_PAYEES,
        count: DEV_PAYEES.length,
        next: null,
        previous: null,
      });
    }
  }
  if (trimmed.startsWith("/contracts/payees/")) {
    const id = trimmed.slice("/contracts/payees/".length);
    const found = DEV_PAYEES.find((p) => p.id === id);
    return found ? jsonResponse(found) : jsonResponse({ detail: "Not found." }, 404);
  }

  // Supported vendors — a bare array (NOT paginated), per vendorsApi.
  if (trimmed === "/imports/vendors" && method === "GET") {
    return jsonResponse(DEV_VENDORS);
  }

  // --- Import batches (the "Upload new statement" wizard) ------------------
  // Auto-import path: analyzing → analyzed (no catalog changes → auto-confirm)
  // → importing → completed. Status advances by elapsed time on each poll.
  if (trimmed === "/imports/batches" && method === "POST") {
    return jsonResponse(createImportBatch(body), 201);
  }
  if (trimmed.startsWith("/imports/batches/")) {
    const rest = trimmed.slice("/imports/batches/".length);
    const [id, sub] = rest.split("/");

    if (sub === "catalog-preview" && method === "GET") {
      // No catalog changes → can_auto_import skips the review step.
      return jsonResponse(EMPTY_CATALOG_PREVIEW);
    }
    if (sub === "summary" && method === "GET") {
      return jsonResponse(DEV_IMPORT_SUMMARY);
    }
    if (sub === "confirm" && method === "POST") {
      const batch = advanceImportBatch(id, "importing");
      return batch ? jsonResponse({}) : jsonResponse({ detail: "Not found." }, 404);
    }
    if (!sub && method === "GET") {
      const batch = pollImportBatch(id);
      return batch ? jsonResponse(batch) : jsonResponse({ detail: "Not found." }, 404);
    }
  }

  // --- Multi-statement settlement ------------------------------------------
  // One distributor payment can settle several statements of the same vendor.
  // Splitting the difference is the backend's job, so both the preview and the
  // settle call go through settlementSplit() — the UI never recomputes it.
  if (trimmed === "/revenue/statements/settlement-preview" && method === "POST") {
    const preview = settlementSplit(body?.statement_ids ?? [], body?.amount_received);
    return preview ? jsonResponse(preview) : jsonResponse({ detail: "Not found." }, 404);
  }

  if (trimmed === "/revenue/statements/settle" && method === "POST") {
    if (isSimulateSettleError()) {
      return jsonResponse({ detail: "Simulated failure (prototype)." }, 500);
    }
    const preview = settlementSplit(body?.statement_ids ?? [], body?.amount_received);
    if (!preview) return jsonResponse({ detail: "Not found." }, 404);
    // Every listed statement is settled in full, at its share of the payment.
    const list = loadRevenue();
    for (const row of preview.rows) {
      const idx = list.findIndex((s) => s.id === row.statement);
      if (idx === -1) continue;
      list[idx] = {
        ...list[idx],
        amount_received: row.received,
        date_received: body?.date_received ?? null,
        exchange_rate: body?.exchange_rate ?? list[idx].exchange_rate,
        difference_reason: body?.reason ?? null,
        updated_at: new Date().toISOString(),
      };
    }
    saveRevenue(list);
    return jsonResponse(preview);
  }

  // Revenue statements collection: /revenue/statements
  if (trimmed === "/revenue/statements") {
    if (method === "GET") {
      let list = loadRevenue();

      // Search by name / file name (q)
      const q = query.get("q")?.toLowerCase();
      if (q) {
        list = list.filter((s) =>
          `${s.name ?? ""} ${s.file_name ?? ""}`.toLowerCase().includes(q),
        );
      }

      // Filter by enum status (legacy param: "draft,ready,…")
      const statusParam = query.get("status");
      if (statusParam) {
        const allowed = new Set(statusParam.split(","));
        list = list.filter((s) => allowed.has(s.status));
      }

      // Filter by derived received status: draft (no amount received) / confirmed.
      const receivedStatus = query.get("received_status");
      if (receivedStatus) {
        const allowed = new Set(receivedStatus.split(","));
        list = list.filter((s) => allowed.has(s.amount_received ? "confirmed" : "draft"));
      }

      // Filter by vendor (comma-separated ids).
      const vendorParam = query.get("vendor");
      if (vendorParam) {
        const allowed = new Set(vendorParam.split(","));
        list = list.filter((s) => allowed.has(s.vendor));
      }

      // Filter by statement period range (YYYY-MM-DD string compare is ordered).
      const periodAfter = query.get("period_start_after");
      const periodBefore = query.get("period_end_before");
      if (periodAfter) list = list.filter((s) => (s.period_start ?? "") >= periodAfter);
      if (periodBefore) list = list.filter((s) => (s.period_end ?? "") <= periodBefore);

      // Sort by ordering field; supports computed "difference" + numeric amounts.
      const ordering = query.get("ordering");
      if (ordering) {
        const desc = ordering.startsWith("-");
        const field = desc ? ordering.slice(1) : ordering;
        const sortValue = (s: any): string => {
          if (field === "difference") {
            if (!s.amount_received) return "";
            return String((parseFloat(s.amount_received) || 0) - (parseFloat(s.revenue) || 0));
          }
          return String(s[field] ?? "");
        };
        list = [...list].sort((a, b) => {
          const cmp = sortValue(a).localeCompare(sortValue(b), undefined, { numeric: true });
          return desc ? -cmp : cmp;
        });
      } else {
        // Default ordering: newest statement period first, so the most recent
        // work is at the top of the list. Same period → newest import first.
        list = [...list].sort((a, b) => {
          const byPeriod = String(b.period_start ?? "").localeCompare(String(a.period_start ?? ""));
          return byPeriod !== 0
            ? byPeriod
            : String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""));
        });
      }

      return jsonResponse(paginated(list));
    }
  }

  // Revenue statement detail + sub-resources: /revenue/statements/:id[/sub]
  if (trimmed.startsWith("/revenue/statements/")) {
    const rest = trimmed.slice("/revenue/statements/".length);
    const [id, sub] = rest.split("/");

    // Sub-resources powering the three details tabs (match before the item route).
    if (sub === "top-tracks" && method === "GET") return jsonResponse(paginated(DEV_STATEMENT_TRACKS));
    if (sub === "track-revenue" && method === "GET")
      return jsonResponse(paginated(DEV_STATEMENT_TRACKS));
    if (sub === "contributors-revenue" && method === "GET")
      return jsonResponse(paginated(DEV_STATEMENT_CONTRIBUTORS));

    // Item: /revenue/statements/:id
    if (!sub) {
      const list = loadRevenue();
      const idx = list.findIndex((s) => s.id === id);
      if (method === "GET") {
        return idx === -1
          ? jsonResponse({ detail: "Not found." }, 404)
          : jsonResponse(list[idx]);
      }
      if (method === "PATCH") {
        if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
        // Prototype-only: a mark-as-settled PATCH carries amount_received. When
        // the "Simulate error" toggle is on, fail it (500) so the failed state
        // and its error toast are reachable — the prototype never fails otherwise.
        if (body?.amount_received != null && isSimulateSettleError()) {
          return jsonResponse({ detail: "Simulated failure (prototype)." }, 500);
        }
        list[idx] = { ...list[idx], ...body, updated_at: new Date().toISOString() };
        saveRevenue(list);
        return jsonResponse(list[idx]);
      }
      if (method === "DELETE") {
        if (idx !== -1) {
          list.splice(idx, 1);
          saveRevenue(list);
        }
        return jsonResponse(null, 204);
      }
    }
  }

  // --- Royalty statements: list + detail + html preview + actions ---
  if (trimmed === "/revenue/royalty-statements" && method === "GET") {
    let list = loadRoyalty();
    const q = (query.get("search") || query.get("q") || "").toLowerCase();
    if (q) {
      list = list.filter((s) =>
        `${s.statement_no} ${s.payee_details?.name ?? ""} ${s.contract_details?.name ?? ""}`
          .toLowerCase()
          .includes(q),
      );
    }
    const statusParam = query.get("status");
    if (statusParam) {
      const allowed = new Set(statusParam.split(","));
      list = list.filter((s) => allowed.has(s.status));
    }
    const payeesParam = query.get("payees");
    if (payeesParam) {
      const allowed = new Set(payeesParam.split(","));
      list = list.filter((s) => allowed.has(s.payee));
    }
    const after = query.get("period_start_after");
    const before = query.get("period_end_before");
    if (after) list = list.filter((s) => (s.period_start ?? "") >= after);
    if (before) list = list.filter((s) => (s.period_end ?? "") <= before);
    const ordering = query.get("ordering");
    if (ordering) {
      const desc = ordering.startsWith("-");
      const field = desc ? ordering.slice(1) : ordering;
      list = [...list].sort((a, b) => {
        const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""), undefined, {
          numeric: true,
        });
        return desc ? -cmp : cmp;
      });
    }
    return jsonResponse(paginated(list));
  }
  if (trimmed.startsWith("/revenue/royalty-statements/")) {
    const rest = trimmed.slice("/revenue/royalty-statements/".length);
    const [id, sub] = rest.split("/");
    const list = loadRoyalty();
    const idx = list.findIndex((s) => s.id === id);

    if (sub === "html" && method === "GET") {
      return idx === -1
        ? jsonResponse({ detail: "Not found." }, 404)
        : jsonResponse({ html: royaltyStatementHtml(list[idx]) });
    }
    if (sub === "mark-sent" && method === "POST") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      list[idx] = { ...list[idx], status: "sent", updated_at: new Date().toISOString() };
      saveRoyalty(list);
      return jsonResponse(list[idx]);
    }
    if (sub === "mark-paid" && method === "POST") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      list[idx] = { ...list[idx], status: "paid", updated_at: new Date().toISOString() };
      saveRoyalty(list);
      return jsonResponse(list[idx]);
    }
    if (!sub) {
      if (method === "GET") {
        return idx === -1 ? jsonResponse({ detail: "Not found." }, 404) : jsonResponse(list[idx]);
      }
      if (method === "PATCH") {
        if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
        list[idx] = { ...list[idx], ...body, updated_at: new Date().toISOString() };
        saveRoyalty(list);
        return jsonResponse(list[idx]);
      }
      if (method === "DELETE") {
        if (idx !== -1) {
          list.splice(idx, 1);
          saveRoyalty(list);
        }
        return jsonResponse(null, 204);
      }
    }
  }

  // Collection: /contracts
  if (trimmed === "/contracts") {
    if (method === "GET") {
      let list = [...DEV_SCENARIO_CONTRACTS, ...loadContracts()];

      // Search by name (q)
      const q = query.get("q")?.toLowerCase();
      if (q) list = list.filter((c) => String(c.name ?? "").toLowerCase().includes(q));

      // Filter by status (comma-separated, e.g. "draft,active")
      const statusParam = query.get("status");
      if (statusParam) {
        const allowed = new Set(statusParam.split(","));
        list = list.filter((c) => allowed.has(c.status));
      }

      // Sort by ordering field ("status", "-status", "name", "start_date", …)
      const ordering = query.get("ordering");
      if (ordering) {
        const desc = ordering.startsWith("-");
        const field = desc ? ordering.slice(1) : ordering;
        list = [...list].sort((a, b) => {
          const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""));
          return desc ? -cmp : cmp;
        });
      }

      return jsonResponse({ results: list, count: list.length, next: null, previous: null });
    }
    if (method === "POST") {
      const list = loadContracts();
      const created = buildContract(body);
      list.unshift(created);
      saveContracts(list);
      return jsonResponse(created, 201);
    }
  }

  // Item: /contracts/:id
  if (trimmed.startsWith("/contracts/")) {
    const id = trimmed.slice("/contracts/".length);

    // Scenario contracts are read-only fixtures for the create-statement panel.
    const scenario = DEV_SCENARIO_CONTRACTS.find((c) => c.id === id);
    if (scenario && method === "GET") return jsonResponse(scenario);

    const list = loadContracts();
    const idx = list.findIndex((c) => c.id === id);

    if (method === "GET") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      return jsonResponse({
        ...list[idx],
        related_recordings: [],
        related_releases: [],
        has_revenue: false,
        has_expenses: false,
      });
    }
    if (method === "PUT" || method === "PATCH") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      const updated = buildContract(body, list[idx]);
      list[idx] = updated;
      saveContracts(list);
      return jsonResponse(updated);
    }
    if (method === "DELETE") {
      if (idx !== -1) {
        list.splice(idx, 1);
        saveContracts(list);
      }
      return jsonResponse(null, 204);
    }
  }

  // Unhandled API route: keep the UI alive with a benign empty payload.
  console.warn(`[devMock] Unhandled ${method} ${path} — returning empty response.`);
  return method === "GET" ? jsonResponse(emptyList) : jsonResponse({});
}

export function installDevApiMock(): void {
  const w = window as unknown as { __devApiMockInstalled?: boolean };
  if (w.__devApiMockInstalled) return;
  w.__devApiMockInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === "string" ? input : input instanceof Request ? input.url : String(input);

    // Only intercept calls to our API base; everything else (Vite HMR, assets) passes through.
    if (!url.startsWith(API_BASE_URL)) return originalFetch(input, init);

    const method = (
      init?.method ??
      (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const [path, queryString = ""] = url.slice(API_BASE_URL.length).split("?");
    const query = new URLSearchParams(queryString);

    let body: any;
    const rawBody = init?.body ?? (input instanceof Request ? undefined : undefined);
    if (typeof rawBody === "string") {
      try {
        body = JSON.parse(rawBody);
      } catch {
        body = undefined;
      }
    } else if (rawBody instanceof FormData) {
      // Flatten multipart uploads (e.g. import batches) — files become their name.
      body = {};
      rawBody.forEach((value, key) => {
        body[key] = value instanceof File ? value.name : value;
      });
    }

    const handled = route(method, path, body, query);
    return handled ?? originalFetch(input, init);
  };

  console.warn("[devMock] In-browser contracts + revenue API mock active (no backend on :8000).");
}
